import React, { useMemo } from 'react';
import {
  Grid, Typography, Card, CardContent, Chip, Box, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { RegiaoVendasDrillDownContentProps } from '../../types/regiaoVendasDrillDown';
import { useDataFormatters } from '../../Hooks/useDataFormatters';
import { formatYAxisValue } from '../DrillDown/formatters';

const RegiaoVendasDrillDownContent: React.FC<RegiaoVendasDrillDownContentProps> = ({ data }) => {
  const { formatCurrency, formatNumber } = useDataFormatters();
  
  const formatPercent = (value: number) => `${value.toFixed(2)}%`;

  const resumo = data.regiao ?? (data as any).cidade ?? null;
  const tituloResumo =
    resumo?.uf ??
    resumo?.nome ??
    'N/A';

  const evolucaoData = useMemo(() => {
    if (!data.evolucao_mensal || !Array.isArray(data.evolucao_mensal)) {
      return [{
        id: 'Vendas',
        data: []
      }];
    }
    
    // Filtrar e validar os dados antes de mapear
    const validData = data.evolucao_mensal.filter(item => 
      item && 
      typeof item === 'object' && 
      item.periodo !== undefined && 
      item.vendas !== undefined &&
      item.periodo !== null &&
      item.vendas !== null
    );
    
    const mappedData = validData.map(item => ({ 
      x: String(item.periodo), 
      y: Number(item.vendas) || 0 
    }));
    
    return [{
      id: 'Vendas',
      data: mappedData
    }];
  }, [data.evolucao_mensal]);

  const { listaMunicipios, tituloMunicipios } = useMemo(() => {
    if (data.top_municipios && Array.isArray(data.top_municipios) && data.top_municipios.length > 0) {
      return {
        tituloMunicipios: 'Top Municípios',
        listaMunicipios: data.top_municipios
          .map(b => ({ municipio: b.municipio, 'Total Vendas': b.total_vendas }))
          .sort((a, b) => a['Total Vendas'] - b['Total Vendas'])
      };
    }

    if (data.top_bairros && Array.isArray(data.top_bairros) && data.top_bairros.length > 0) {
      return {
        tituloMunicipios: 'Top Bairros',
        listaMunicipios: data.top_bairros
          .map(b => ({ municipio: b.bairro, 'Total Vendas': b.total_vendas }))
          .sort((a, b) => a['Total Vendas'] - b['Total Vendas'])
      };
    }

    return {
      tituloMunicipios: 'Top Municípios',
      listaMunicipios: [] as { municipio: string; 'Total Vendas': number }[]
    };
  }, [data.top_municipios, data.top_bairros]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Resumo da Região: {tituloResumo}
            </Typography>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid item xs={6} sm={4} md={3}>
                <Chip 
                    label={`Total Vendas: ${formatCurrency(resumo?.total_vendas || 0)}`} 
                  color="success" 
                  sx={{ width: '100%' }} 
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Chip 
                    label={`Participação: ${formatPercent(resumo?.participacao_percentual || 0)}`} 
                  color="primary" 
                  sx={{ width: '100%' }} 
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Chip 
                    label={`Ticket Médio: ${formatCurrency(resumo?.ticket_medio || 0)}`} 
                  color="info" 
                  sx={{ width: '100%' }} 
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Chip 
                    label={`Clientes: ${formatNumber(resumo?.clientes_distintos || 0)}`} 
                  color="warning" 
                  sx={{ width: '100%' }} 
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Chip 
                    label={`Documentos: ${formatNumber(resumo?.total_documentos || 0)}`} 
                  color="secondary" 
                  sx={{ width: '100%' }} 
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Chip 
                    label={`Produtos: ${formatNumber(resumo?.produtos_distintos || 0)}`} 
                  color="default" 
                  sx={{ width: '100%' }} 
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Chip 
                    label={`ICMS Débito: ${formatCurrency(resumo?.total_debito_icms || 0)}`} 
                  color="error" 
                  sx={{ width: '100%' }} 
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Chip 
                    label={`Qtde Total: ${formatNumber(resumo?.quantidade_total || 0)}`} 
                  color="success" 
                  variant="outlined"
                  sx={{ width: '100%' }} 
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
              <Typography variant="h6" gutterBottom>Evolução das Vendas</Typography>
            <Box sx={{ height: 300 }}>
              {evolucaoData[0]?.data && evolucaoData[0].data.length > 0 ? (
                <ResponsiveLine
                  data={evolucaoData}
                  margin={{ top: 20, right: 110, bottom: 50, left: 80 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', stacked: false }}
                  axisLeft={{ format: formatYAxisValue }}
                  colors={{ scheme: 'category10' }}
                  pointSize={10}
                  useMesh={true}
                />
              ) : (
                <Box 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <Typography>Nenhum dado de evolução disponível</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardContent>
              <Typography variant="h6" gutterBottom>{tituloMunicipios}</Typography>
            <Box sx={{ height: 400 }}>
                {listaMunicipios.length > 0 ? (
                <ResponsiveBar
                    data={listaMunicipios}
                  keys={['Total Vendas']}
                  layout="horizontal"
                    indexBy="municipio"
                    margin={{ top: 20, right: 130, bottom: 50, left: 220 }}
                  padding={0.3}
                  colors={{ scheme: 'nivo' }}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Valor R$',
                    legendPosition: 'middle',
                    legendOffset: 40,
                    format: formatYAxisValue
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                      tickRotation: 0
                  }}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                />
              ) : (
                <Box 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <Typography>Nenhum dado disponível</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Top Clientes</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>CNPJ/CPF</TableCell>
                    <TableCell align="right">Valor Vendas</TableCell>
                    <TableCell align="right">Documentos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.top_clientes?.slice(0, 10).map((cliente, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {cliente.nome}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {cliente.cnpj_cpf}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(cliente.valor_vendas)}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(cliente.documentos)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default RegiaoVendasDrillDownContent; 